import {AppInitializeService} from '../app-initialize-service';
import {inject, Injectable} from '../di';
import {ConfigService} from '../config/config-service';
import {StorageService} from '../storage-service';
import {BehaviorSubject, combineLatest, distinctUntilChanged, map} from 'rxjs';
import {enDefaultTranslations} from './default-translations/en-default-translations';
import {getJson} from '../utils/get-json';
import {itDefaultTranslations} from './default-translations/it-default-translations';
import {ruDefaultTranslations} from './default-translations/ru-default-translations';
import {zhDefaultTranslations} from './default-translations/zh-default-translations';
import {Logger} from '../logger';
import {StructService} from '../struct/struct-service';
import {deepEqual} from 'fast-equals';
import {deepMerge} from '../utils/deep-merge';
import {LegacyLogService} from '../logger/legacy-log-service';
import {LegacyPersistentLogCategory, LegacyPersistentLogService} from '../logger/legacy-persistent-log-service';

/**
 * Internationalization service.
 * Contains all the logic for internationalization (localization, translations, and cultural adaptations).
 */
@Injectable()
export class I18nService implements AppInitializeService {
  private readonly storageService = inject(StorageService);
  private readonly configService = inject(ConfigService);
  private readonly logger = inject(Logger);
  private readonly legacyLogService = inject(LegacyLogService);
  private readonly legacyPersistentLogService = inject(LegacyPersistentLogService);

  /**
   * List of translations for different locales.
   * The first level keys are locale, the second level keys are localization keys.
   */
  private translations: Record<string, Record<string, string>> = this.getDefaultTranslations();

  /**
   * Application locale, selected by the user or delivered with guest info.
   * Represented by BCP47 language tag.
   * @example 'az-Cyrl-AZ'
   */
  private readonly selectedLocale$ = new BehaviorSubject<string | undefined>(this.getPreservedGuestLocale());

  /**
   * Bulletproof fallback locale.
   */
  private readonly DEFAULT_LOCALE: string = 'en';

  /**
   * A list with the current locale and all possible fallbacks in BCP47 format. No duplicates, in order of use.
   */
  public readonly localeFallbackChain$ = new BehaviorSubject<string[]>(this.getCurrentLocaleFallbackChain());

  /**
   * Get current most specific language
   */
  public get language() {
    return this.localeFallbackChain$.getValue()[0];
  }

  constructor() {
    // Track various locales changes and construct a fallback chain.
    combineLatest([
      this.selectedLocale$.pipe(distinctUntilChanged()),
      // NOTE: decided to skip the default language from struct to avoid circular dependencies and reloads
      this.configService.config$.pipe(
        map((c) => c?.defaults?.language),
        distinctUntilChanged(),
      ),
    ])
      .pipe(
        map(() => this.getCurrentLocaleFallbackChain()),
        distinctUntilChanged(deepEqual),
      )
      .subscribe((fallbackChain) => {
        this.localeFallbackChain$.next(fallbackChain);
        this.setHtmlLangAttr();
      });

    /**
     * Drop current language and use only fallbacks
     * @deprecated legacy. Use
     */
    window.setDefaultLanguage = () => {
      this.clearCurrentLocale();
    };

    /**
     * Get current language.
     * @param {'pms'} [type] set this argument to 'pms' to get PMS guest language. Currently, it is used only for channel sorting.
     * @deprecated legacy. Use locales fallback chain from i18nService.localeFallbackChain$ instead.
     */
    window.get_language = (type?: 'pms') => {
      // TODO refactor to locale chain usage
      // TODO subscribe to Guest.pmsLanguage changes to include it to the chain

      if (type === 'pms' && window.Guest.pmsLanguage !== '') {
        return window.Guest.pmsLanguage;
      }

      return this.localeFallbackChain$.getValue()[0];
    };

    /**
     * Get translation by localization key
     * @param {string} localizationKey localization key
     * @param {string} data additional localization parameter to insert into `{{data}}` constructions in localizations.
     * @deprecated legacy. Use i18nService.translate() instead.
     */
    window.getlang = (localizationKey: string, data: string) => this.translate(localizationKey, data);

    /**
     * Changes the application's language if the requested language is supported.
     * If the requested language is not supported, falls back to the default language.
     * Triggers a language change event, updates storage, and reloads the application
     * only if the language is actually changed and applied.
     *
     * @param {string} language - The language code to switch to.
     * @deprecated legacy. Use i18nService.setCurrentLocale() instead.
     */
    window.change_language = (language: string) => {
      // TODO refactor to use just this.setCurrentLocale when all consumers will migrate to locale chain usage

      // Validate language support before changing
      const structService = inject(StructService);
      const selectableLocales = structService.structure$.getValue()?.language?.map((l) => l.code) || [];
      const currentLanguage = this.localeFallbackChain$.getValue()[0];

      // If the requested language is not supported, use a default language
      if (!selectableLocales.includes(language)) {
        this.logger.debug(`[i18nService] language ${language} not supported`);
        this.legacyLogService.add(`TV: language ${language} not supported`);

        const config = this.configService.config;
        const configLanguage = config.defaults?.language;
        if (currentLanguage !== configLanguage && configLanguage) {
          language = configLanguage;
          this.logger.debug(`[i18nService] applying default locale: ${language}`);
        } else {
          // If a current language is already default and requested is not supported, don't change
          return;
        }
      }

      // If the language is already current, no need to change
      if (language === currentLanguage) {
        return;
      }

      this.setCurrentLocale(language);
    };
  }

  public async init(): Promise<boolean> {
    return await this.loadTranslations();
  }

  /**
   * Get translation by localization key
   * @param {string} localizationKey localization key
   * @param {string | number} [data] additional localization parameter to insert into `{{data}}` constructions in
   *   localizations.
   */
  public translate(localizationKey: string, data?: string | number) {
    const localeFallbackChain = this.localeFallbackChain$.getValue();

    for (let i = 0; i < localeFallbackChain.length; i++) {
      const locale = localeFallbackChain[i];
      let translation: string | undefined = this.translations?.[locale]?.[localizationKey];
      if (translation && typeof translation === 'string') {
        if (data != null) {
          translation = translation.replaceAll('{{data}}', data.toString());
        }
        return translation;
      }
    }

    this.logger.error(
      `[i18nService] Translation not found for "${localizationKey}" key and "${localeFallbackChain.toString()}" locale chain`,
    );
    return `[NO TRANSLATION FOR ${localeFallbackChain[0]}:${localizationKey}]`;
  }

  /**
   * Set guest's current language.
   * @param locale BCP47 language tag
   */
  public setCurrentLocale(locale: string) {
    if (this.selectedLocale$.getValue() === locale) {
      return;
    }

    this.selectedLocale$.next(locale);
    this.storageService.set('language', locale);
    // NOTE: no reload required if every locale consumer will migrate to locale chain usage

    // we also need to call $(HotezaTV).trigger('language', language); to send new locale by websockets
    // TODO refactor to locale chain usage
    $(window.HotezaTV).trigger('language', locale);

    // call vendor module function if present
    if (window._tv_change_language) {
      // TODO refactor to locale chain
      window._tv_change_language(locale);
    }

    setTimeout(() => {
      this.legacyPersistentLogService.add('change language: ' + locale, LegacyPersistentLogCategory.Reload);
      this.legacyLogService.add('TV: reloading, change language: ' + locale);
      this.logger.debug(`[i18nService] reloading, change language: ${locale}`);
      window.reload_app();
    }, 500);
  }

  /**
   * Clear guest current language (default fallbacks will be used).
   */
  public clearCurrentLocale() {
    const currentLocale = this.localeFallbackChain$.getValue()[0];

    this.selectedLocale$.next(undefined);
    this.storageService.remove('language');

    const newCurrentLocale = this.localeFallbackChain$.getValue()[0];

    if (currentLocale !== newCurrentLocale) {
      // we also need to call $(HotezaTV).trigger('language', language); to send new locale by websockets
      // TODO refactor to locale chain usage
      $(window.HotezaTV).trigger('language', newCurrentLocale);

      // call vendor module function if present
      if (window._tv_change_language) {
        // TODO refactor to locale chain
        window._tv_change_language(newCurrentLocale);
      }

      setTimeout(() => {
        this.legacyPersistentLogService.add('change language: ' + newCurrentLocale, LegacyPersistentLogCategory.Reload);
        this.legacyLogService.add('TV: reloading, change language: ' + newCurrentLocale);
        this.logger.debug(`[i18nService] reloading, change language: ${newCurrentLocale}`);
        window.reload_app();
      }, 500);
    }
  }

  /**
   * Filter the incoming array of BCP47 locales (possibly with the wrong case) to include only those that are in the
   * current application locale chain.
   */
  public filterApplicableLocales(locales: string[]): string[] {
    const localeChain = this.localeFallbackChain$.getValue();
    const result: string[] = [];
    for (let i = 0; i < localeChain.length; i++) {
      const appLocale = localeChain[i].toLowerCase();
      for (let j = 0; j < locales.length; j++) {
        const locale = locales[j];
        if (locale.toLowerCase() === appLocale && !result.includes(locale)) {
          result.push(locale);
        }
      }
    }
    return result;
  }

  /**
   * Find the most applicable locale (to the current application locale chain) in the input BCP47 array of locales
   * (possibly with an incorrect case).
   */
  public getMostApplicableLocale(locales: string[]): string | undefined {
    const localeChain = this.localeFallbackChain$.getValue();
    for (let i = 0; i < localeChain.length; i++) {
      const appLocale = localeChain[i].toLowerCase();
      for (let j = 0; j < locales.length; j++) {
        const locale = locales[j];
        if (locale.toLowerCase() === appLocale) {
          return locale;
        }
      }
    }
    return undefined;
  }

  private async loadTranslations() {
    const contentUrl = 'c/';
    const url = contentUrl + 'lang.json';
    try {
      this.logger.debug(`[i18nService] Loading localization file from content: "${url}"...`);
      const translations: Record<string, Record<string, string>> = await getJson(url);

      const config = this.configService.config;

      // Change escaped quotes from CMS like \" to "
      for (const localeKey in translations) {
        if (translations[localeKey]) {
          for (const transKey in translations[localeKey]) {
            translations[localeKey][transKey] = translations[localeKey][transKey].replaceAll('\\"', '"');
          }
        }
      }

      // First, apply default translations (see getDefaultTranslations)
      // Then apply translations from struct (c/lang.json)
      // Then apply config.lang_override (deprecated)
      // Then apply config.lang (top priority)
      // NOTE: merging already supports deep JSONs
      this.translations = [translations, config.lang_override, config.lang].reduce(
        (acc, translations) => (translations ? (deepMerge(acc, translations) as I18nService['translations']) : acc),
        this.translations,
      )!;
    } catch (err) {
      this.logger.error(`[i18nService] Unable to load localization file from content: ${url}`, err);
      return false;
    }
    return true;
  }

  /**
   * Get a list of locales with all possible fallbacks in BCP47 format. No duplicates, in order of use. The first
   * element of the array is the current (most specific) locale, followed by possible replacements. If you need to load,
   * for example, a file with localizations for some framework, then you need to take this list and iterate over it
   * until you come across the first locale for which you have a translation. If not, you will have to determine which
   * locale will be the default yourself.
   */
  private getCurrentLocaleFallbackChain(): string[] {
    return [
      ...this.getLocaleFallbackChain(this.selectedLocale$.getValue()), // locale selected by guest
      ...this.getLocaleFallbackChain(this.configService.config.defaults?.language), // config default locale
      ...this.getLocaleFallbackChain(this.DEFAULT_LOCALE), // last resort default locale
    ].filter((v, i, arr) => arr.indexOf(v) === i); // and remove duplicates
  }

  /**
   * Get the locale fallback chain for the given locale, including the given locale.
   * @param locale BCP47 language tag
   * @example getLocaleFallbackChain('zh-Hans-CN') // -> ['zh-Hans-CN', 'zh-Hans', 'zh-CN', 'zh']
   */
  private getLocaleFallbackChain(locale: string | undefined): string[] {
    if (!locale) {
      return [];
    }
    const fallbacks: string[] = [locale];
    const parts = locale.split('-');

    for (let i = parts.length - 1; i > 0; i--) {
      const strippedPart = parts.pop()!;
      fallbacks.push(parts.join('-'));

      // For script and region case, we need to fall back to both variants (language+script and language+region), but a
      //  script has higher priority. So we just add language+region to the list after the language+script variant.
      if (i === 2 && strippedPart.length === 2 && parts[1].length === 4) {
        fallbacks.push(`${parts[0]}-${strippedPart}`);
      }
    }

    return fallbacks;
  }

  /**
   * Get the default list of translations that we should have in case nothing could load at all.
   */
  private getDefaultTranslations(): Record<string, Record<string, string>> {
    return {
      en: enDefaultTranslations,
      it: itDefaultTranslations,
      ru: ruDefaultTranslations,
      zh: zhDefaultTranslations,
    };
  }

  private getPreservedGuestLocale(): string | undefined {
    return this.storageService.getString('language') || undefined;
  }

  /**
   * Set lang attribute on html element.
   */
  private setHtmlLangAttr() {
    if (document.documentElement) {
      const lang = this.localeFallbackChain$.getValue()[0];
      document.documentElement.setAttribute('lang', lang);
    }
  }
}

export enum LanguageCode {
  EN = 'en', // English
  SQ = 'sq', // Albanian (shqip)
  AR = 'ar', // Arabic (العربية)
  HY = 'hy', // Armenian (հայdelays)
  AZ = 'az', // Azerbaijani
  ZH = 'zh', // Chinese Simplified (简体中文)
  ZH_TW = 'zh-TW', // Chinese Traditional (繁體中文)
  HR = 'hr', // Croatian (Hrvatski)
  DA = 'da', // Danish (Dansk)
  FI = 'fi', // Finnish (Suomen)
  FR = 'fr', // French (Français)
  FY = 'fy', // Frisian (Frysk)
  KA = 'ka', // Georgian (ქართული)
  DE = 'de', // German (Deutsch)
  EL = 'el', // Greek (ελληνικά)
  HU = 'hu', // Hungarian (magyar nyelv)
  IT = 'it', // Italian (Italiano)
  JA = 'ja', // Japanese (日本語)
  KZ = 'kz', // Kazakh (Қazақ тілі)
  KM = 'km', // Khmer / Cambodian (ភាសាខ្មែរ)
  KO = 'ko', // Korean (한국어)
  KY = 'ky', // Kyrgyz (Кыргыз тили)
  MK = 'mk', // Macedonian (Македонски)
  M0 = 'm0', // Montenegrin (Crnogorski) - custom code, no ISO 639-1
  NL = 'nl', // Dutch (Nederlands)
  NO = 'no', // Norwegian (Norsk)
  FA = 'fa', // Persian Fārsi (فارسی)
  PL = 'pl', // Polish (Język polski)
  PT = 'pt', // Portuguese (Português)
  RO = 'ro', // Romanian (Roumanian)
  RU = 'ru', // Russian (Русский)
  SR = 'sr', // Serbian (Српски)
  SI = 'si', // Slovenian (Slovenski jezik)
  SK = 'sk', // Slovak (Slovenský jazyk)
  ES = 'es', // Spanish (Español)
  SW = 'sw', // Swahili (Kiswahili)
  SV = 'sv', // Swedish (Svenska)
  TH = 'th', // Thai (ภาษาไทย)
  TR = 'tr', // Turkish (Türkçe)
  UK = 'uk', // Ukrainian (Українська)
  VI = 'vi', // Vietnamese (Việt Nam)
  TK = 'tk', // Turkmen (Türkmençe)
  UZ = 'uz', // Uzbek (Oʻzbekcha)
  BE = 'be', // Belarusian (Беларуская мова)
  MS = 'ms', // Malay language (Bahasa Melayu)
}

declare global {
  interface Window {
    setDefaultLanguage: () => void;
    get_language: (type?: 'pms') => string;
    getlang: (localizationKey: string, data: string) => string;
    change_language: (language: string) => void;
  }
}
